import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Badge,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import ImageIcon from "@mui/icons-material/Image";
import UploadIcon from "@mui/icons-material/Upload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AppRegistrationOutlinedIcon from "@mui/icons-material/AppRegistrationOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import SettingsIcon from "@mui/icons-material/Settings";
import EmailIcon from "@mui/icons-material/Email";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { ImageUploadModal } from "../../components/admin/ImageUploadModal";
import { useAdminStats } from "../../hooks/useAdminStats";
import { useUnviewedSubmissionsCount } from "../../hooks/useContactSubmissions";

export const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const { data: stats, isLoading, error } = useAdminStats();
  const { data: unviewedCount } = useUnviewedSubmissionsCount();

  return (
    <AdminLayout>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          Dashboard
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4,
          }}
        >
          Overview of your photography portfolio
        </Typography>

        {/* Stats Overview - Clean Minimal Design */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="error">
              Failed to load statistics. Please try again.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 4 },
              mb: { xs: 4, sm: 6 },
              flexWrap: "wrap",
            }}
          >
            {/* Total Galleries */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <PhotoLibraryIcon
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  color: "primary.main",
                }}
              />
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                    color: "text.primary",
                    lineHeight: 1.4,
                  }}
                >
                  {stats?.total_galleries ?? 0}{" "}
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", fontWeight: 400 }}
                  >
                    Galleries
                  </Box>
                </Typography>
              </Box>
            </Box>

            {/* Total Images */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <ImageIcon
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  color: "success.main",
                }}
              />
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                    color: "text.primary",
                    lineHeight: 1.4,
                  }}
                >
                  {stats?.total_images ?? 0}{" "}
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", fontWeight: 400 }}
                  >
                    Images
                  </Box>
                </Typography>
              </Box>
            </Box>

            {/* Storage */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <UploadIcon
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  color: "warning.main",
                }}
              />
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                    color: "text.primary",
                    lineHeight: 1.4,
                  }}
                >
                  {stats?.total_storage_formatted ?? "0 B"}{" "}
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", fontWeight: 400 }}
                  >
                    Storage
                  </Box>
                </Typography>
              </Box>
            </Box>

            {/* Views (30 days) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <VisibilityIcon
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  color: "info.main",
                }}
              />
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                    color: "text.primary",
                    lineHeight: 1.4,
                  }}
                >
                  {stats?.recent_views_30d?.toLocaleString() ?? 0}{" "}
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", fontWeight: 400 }}
                  >
                    Views (30d)
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Quick Actions */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
            }}
          >
            Quick Actions
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.5, sm: 2 },
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() => setUploadModalOpen(true)}
              variant="text"
              disableRipple
              startIcon={<CloudUploadOutlinedIcon />}
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                px: { xs: 1, sm: 0 },
                py: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: "44px", sm: "auto" },
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                  "&::after": {
                    width: "100%",
                  },
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                  fontSize: "1rem",
                  marginRight: "8px",
                  transition: "color 0.2s ease",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
              }}
            >
              Upload Images
            </Button>
            <Button
              component={Link}
              to="/admin/galleries"
              disableRipple
              variant="text"
              startIcon={<CollectionsOutlinedIcon />}
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                px: { xs: 1, sm: 0 },
                py: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: "44px", sm: "auto" },
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                  "&::after": {
                    width: "100%",
                  },
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                  fontSize: "1rem",
                  marginRight: "8px",
                  transition: "color 0.2s ease",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
              }}
            >
              Manage Galleries
            </Button>
            <Button
              component={Link}
              to="/admin/images"
              disableRipple
              variant="text"
              startIcon={<ManageSearchIcon />}
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                px: { xs: 1, sm: 0 },
                py: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: "44px", sm: "auto" },
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                  "&::after": {
                    width: "100%",
                  },
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                  fontSize: "1rem",
                  marginRight: "8px",
                  transition: "color 0.2s ease",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
              }}
            >
              Manage Images
            </Button>
            <Button
              onClick={() => navigate("/admin/categories")}
              variant="text"
              disableRipple
              startIcon={<AppRegistrationOutlinedIcon />}
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                px: { xs: 1, sm: 0 },
                py: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: "44px", sm: "auto" },
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                  "&::after": {
                    width: "100%",
                  },
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                  fontSize: "1rem",
                  marginRight: "8px",
                  transition: "color 0.2s ease",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
              }}
            >
              Manage Categories
            </Button>
            <Button
              component={Link}
              to="/admin/image-settings"
              variant="text"
              disableRipple
              startIcon={<WallpaperIcon />}
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                px: { xs: 1, sm: 0 },
                py: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: "44px", sm: "auto" },
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                  "&::after": {
                    width: "100%",
                  },
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                  fontSize: "1rem",
                  marginRight: "8px",
                  transition: "color 0.2s ease",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
              }}
            >
              Manage Image Settings
            </Button>
            <Button
              component={Link}
              to="/admin/contact-submissions"
              variant="text"
              disableRipple
              startIcon={
                unviewedCount !== undefined && unviewedCount > 0 ? (
                  <Badge
                    badgeContent={unviewedCount}
                    color="success"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "success.main",
                        color: "white",
                        fontSize: "0.7rem",
                        minWidth: "18px",
                        height: "18px",
                        padding: "0 4px",
                      },
                    }}
                  >
                    <EmailIcon />
                  </Badge>
                ) : (
                  <EmailIcon />
                )
              }
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                px: { xs: 1, sm: 0 },
                py: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: "44px", sm: "auto" },
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                  "&::after": {
                    width: "100%",
                  },
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                  fontSize: "1rem",
                  marginRight: "8px",
                  transition: "color 0.2s ease",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
              }}
            >
              Contact Submissions
            </Button>
            <Button
              component={Link}
              to="/admin/settings"
              variant="text"
              disableRipple
              startIcon={<SettingsIcon />}
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                px: { xs: 1, sm: 0 },
                py: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: "44px", sm: "auto" },
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "transparent",
                  "&::after": {
                    width: "100%",
                  },
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                  fontSize: "1rem",
                  marginRight: "8px",
                  transition: "color 0.2s ease",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
              }}
            >
              Site Settings
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Image Upload Modal */}
      <ImageUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
      />
    </AdminLayout>
  );
};
